import React from 'react';
import PropTypes from 'prop-types';

// Components
import StatusChip, { connectionChip } from './StatusChip';

// Constants
import { isNotNull } from '../constants';
import { T, FONT_MONO } from '../constants/tokens';

// Icons
import Hallway from '../images/customIcons/Hallway.png';
import Room from '../images/customIcons/Room.png';
import Door from '../images/customIcons/Door.png';

function getIcon(hubType) {
  switch (hubType) {
    case 'Hallway':
      return Hallway;
    case 'Room':
      return Room;
    case 'Door':
      return Door;
    default:
      return;
  }
}

/** Connection and firmware, stacked. Used in both layouts. */
function StatusLines({ hub, align = 'flex-end' }) {
  const connection = connectionChip(hub);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: align, gap: 2 }}>
      {connection && <StatusChip tone={connection.tone} label={connection.label} />}
      {isNotNull(hub.getFirmwareVersion()) &&
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.mutedSoft }}>
          v{hub.getFirmwareVersion()}
        </span>}
    </div>
  );
}

StatusLines.propTypes = {
  hub: PropTypes.object.isRequired,
  align: PropTypes.string
};

/**
 * One hub inside a detail panel: type icon, serial, then its state.
 *
 * The old panel layout was a rounded, drop-shadowed tile with an 80px icon.
 * v7 makes it a hairline row so several hubs can sit in a panel without it
 * turning into a stack of cards.
 */
function PanelRow({ hub }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
        borderBottom: '1px solid ' + T.hairlineSoft
      }}
    >
      <img
        src={getIcon(hub.getHubType())}
        alt=''
        style={{ width: 28, height: 28, flex: 'none', objectFit: 'contain' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '20px',
            color: T.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {hub.getSerialNumber()}
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            textTransform: 'uppercase',
            color: T.muted
          }}
        >
          {hub.getHubType()}
        </div>
      </div>
      <StatusLines hub={hub} />
    </div>
  );
}

PanelRow.propTypes = {
  hub: PropTypes.object.isRequired
};

export default function HubStatusWidget({ style = {}, hub, hubs, isInfoPanel = false, showLabel = false }) {
  const all = hubs ?? (hub ? [hub] : []);
  if (all.length === 0) return null;

  if (isInfoPanel) {
    return (
      <div style={style}>
        {all.map((h, index) => <PanelRow key={index} hub={h} />)}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, ...style }}>
      {showLabel &&
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'uppercase',
            color: T.muted
          }}
        >
          Hub status
        </span>}
      {all.map((h, index) => <StatusLines key={index} hub={h} />)}
    </div>
  );
}

HubStatusWidget.propTypes = {
  hub: PropTypes.object,
  hubs: PropTypes.array,
  isInfoPanel: PropTypes.bool,
  showLabel: PropTypes.bool,
  style: PropTypes.object
};
